import { ConditionsNew, DraftCondition, MarkupConditionNew } from '@retail/calculus/types';
import { Image, StackedTextCell } from '@retail/components';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@retail/products/utils';
import { useAppTFunction } from '@retail/app/i18n';
import { Stack, Tooltip, Typography } from '@mui/material';
import { useCallback } from 'react';
import { calculateCoverageFromSalesFactor } from '@retail/utils';
import { SalesPriceConditionValue } from '../SalesPriceConditions';
import { getConditionFromType } from '@retail/calculus/utils';

interface Props {
  condition: DraftCondition;
  conditions: DraftCondition[];
}

export const ChangeType = ({ condition, conditions }: Props) => {
  const t = useAppTFunction();
  const { i18n } = useTranslation();

  const getPurchaseText = (condition: ConditionsNew) =>
    `${condition.purchaseCondition?.agreementText} (${condition.purchaseCondition?.agreementId})`;

  const getFreightText = useCallback(
    (condition: ConditionsNew) =>
      condition.freightCondition?.percentAddon
        ? `${formatPrice(condition.freightCondition?.percentAddon, i18n.language)}%`
        : condition.freightCondition?.fixedAddOn
        ? t('markup.pricePerUnit', {
            price: condition.freightCondition?.fixedAddOn,
            unit: condition.freightCondition?.fixedAddOnUnit,
          })
        : '-',
    [i18n.language, t]
  );

  const getMarkupText = useCallback(
    (condition: ConditionsNew) => {
      if (!condition.markupCondition?.coverageDegree && condition.markupCondition?.salesFactor) {
        const coverageDegree = calculateCoverageFromSalesFactor(
          condition.markupCondition?.salesFactor
        );
        return (
          <Typography variant="subtitle2" pl={3}>{`${formatPrice(
            coverageDegree,
            i18n.language
          )}% (${condition.markupCondition?.salesFactor?.toFixed(4)})`}</Typography>
        );
      }
      return (
        <Typography variant="subtitle2" pl={3}>{`${formatPrice(
          Number(condition.markupCondition?.coverageDegree),
          i18n.language
        )}% (${condition.markupCondition?.salesFactor?.toFixed(4)})`}</Typography>
      );
    },
    [i18n.language]
  );

  const getPriceSuggestionText = (condition: ConditionsNew) => (
    <Stack>
      {condition.priceSourceCondition?.priorities?.map(({ priority, priceSuggestionType }) => {
        return (
          <Stack flexDirection="row" alignItems="center" gap={0.5} key={priority}>
            <Typography variant="subtitle2" color="textSecondary">
              {priority}.{' '}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {priceSuggestionType}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );

  const getSalesPriceConditionText = useCallback(
    (condition: ConditionsNew, replacedMarkup?: MarkupConditionNew) => (
      <SalesPriceConditionValue
        condition={condition}
        draftedMarkup={
          !replacedMarkup
            ? conditions.find(
                (cond) =>
                  cond.condition.bindingTargetId === condition.bindingTargetId &&
                  cond.condition.bindingType === condition.bindingType &&
                  cond.condition.type === 'MARKUP'
              )?.condition.markupCondition
            : undefined
        }
        replacedMarkup={replacedMarkup}
      />
    ),
    [conditions]
  );

  const getCustomerMarkupText = useCallback(
    (condition: ConditionsNew) =>
      condition.customerMarkupCondition?.percent
        ? `${formatPrice(condition.customerMarkupCondition?.percent, i18n.language)}%`
        : condition.customerMarkupCondition?.salesFactor?.toFixed(4),
    [i18n.language]
  );

  const renderConditionTypeInfo = useCallback(
    (condition: ConditionsNew, replacedMarkup?: MarkupConditionNew) => {
      switch (condition.type) {
        case 'PURCHASE':
          return getPurchaseText(condition);
        case 'FREIGHT':
          return getFreightText(condition);
        case 'MARKUP':
          return getMarkupText(condition);
        case 'PRICE_SOURCE':
          return getSalesPriceConditionText(condition, replacedMarkup);
        case 'PRICE_SUGGESTION':
          return getPriceSuggestionText(condition);
        case 'CUSTOMER_MARKUP':
          return getCustomerMarkupText(condition);
        case 'ROUNDING': {
          const roundingRule = condition.roundingCondition;
          return (
            <Stack flexDirection="row" alignItems="center" gap={0.5}>
              <Typography variant="body1">{roundingRule?.ruleSetName}</Typography>
              {roundingRule?.centralOwner && (
                <Tooltip
                  title={t('rounding.ruleOwner.central', {
                    owner: roundingRule.centralOwner.name,
                  })}
                >
                  <Image
                    src={roundingRule.centralOwner.imgUrl}
                    alt={roundingRule.centralOwner.name}
                    width={20}
                    height={20}
                  />
                </Tooltip>
              )}
            </Stack>
          );
        }
        default:
          return '';
      }
    },
    [getCustomerMarkupText, getFreightText, getMarkupText, getSalesPriceConditionText, t]
  );

  const prevValue = !condition.replacesCondition
    ? null
    : renderConditionTypeInfo(condition.replacesCondition, condition.condition.markupCondition);

  const newValue = getConditionFromType(condition.condition)?.isDeletionCondition
    ? null
    : renderConditionTypeInfo(condition.condition);

  return (
    <Stack px={1} display="flex" flexGrow={1}>
      <StackedTextCell
        topLine={newValue}
        bottomLine={prevValue}
        nullableBottomLine
        slotProps={{
          bottomLine: {
            lineThrough:
              !!prevValue || !!getConditionFromType(condition.condition)?.isDeletionCondition,
          },
        }}
      />
    </Stack>
  );
};
